import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MandalaMotif } from "@/components/MotifBackground";
import { Lock } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { user, ready, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (ready && user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-parchment px-5">
      <MandalaMotif className="pointer-events-none absolute -right-24 -top-16 h-[420px] w-[420px] text-[var(--gold)] opacity-[0.06]" />
      <div className="relative z-[2] w-full max-w-md border border-line bg-surface p-8 md:p-10">
        <div className="mb-8 text-center">
          <p className="font-deva text-4xl text-ink">धर्मार्थ</p>
          <p className="mt-2 overline-label text-gold">Editorial Console</p>
        </div>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="mb-1.5 block text-sm text-ink-2">Email</label>
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-line bg-parchment px-4 py-2.5 text-ink focus:border-saffron focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm text-ink-2">Password</label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full border border-line bg-parchment px-4 py-2.5 text-ink focus:border-saffron focus:outline-none"
            />
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-[var(--saffron-dark)]">{error}</p>}
          <button
            data-testid="admin-login-submit"
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 bg-[var(--saffron)] py-3 font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60"
          >
            <Lock className="h-4 w-4" /> {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
